import { useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TransactionStatus, TransactionType, TransactionsLists } from 'vopay';
import { ApprovalPanel, InfoBanner } from '@/components/UI';
import { getFundingSettlementTimeLabel } from '@/hooks/useFields/TransactionCreationFields/useFundingOptionField';
import { useToast } from '@/providers/Toast.provider';
import useFetchFundingOptions from '@/hooks/useApi/useFetchFundingOptions';

type FundingStatusBannerProps = {
  transactionStatus?: TransactionStatus;
  parentTransactionId?: string;
  // Resolved by TransactionDetails.tsx (shared with the rail containers' Credit Card fallback)
  // instead of this component fetching its own copy of the same parent transaction.
  parentTransaction?: TransactionsLists;
  // Callers aren't consistent. keep it string
  currency?: string;
  className?: string;
  eLinxRequestId?: string;
};

// Status-driven banner for the Payment Details (child) side — 5 states per the Transaction
// Confirmation & Details spec. Green "Pending Approval" is unchanged; the rest are new, keyed off
// the funding parent's own status.
export default function FundingStatusBanner({
  transactionStatus,
  parentTransactionId,
  parentTransaction: fundingParentTransaction,
  currency,
  className,
  eLinxRequestId,
}: FundingStatusBannerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Transaction.TransactionHistory.TransactionDetails' });
  const toast = useToast();
  const { fundingOptions, fetchFundingOptions } = useFetchFundingOptions();

  // Allowlist: backend only sets `waiting` while a funding parent is in play. Any other status
  // means the funding story is over or never applied.
  const isWaitingOnFunding = transactionStatus === TransactionStatus.WAITING;
  // A failed-funding payment also transitions to `failed`, not `waiting` — the banner must
  // render for this status too, or the "funding failed" copy is unreachable.
  const isPaymentFailed = transactionStatus === TransactionStatus.FAILED;

  useEffect(() => {
    if (!parentTransactionId || !isWaitingOnFunding) return;

    const loadFundingOptions = async () => {
      try {
        await fetchFundingOptions({ Currency: currency || 'CAD' });
      } catch {
        toast.neutral(t('FundingStatusBanner.fetchError', { defaultValue: 'Could not load the funding transaction' }));
      }
    };

    loadFundingOptions();
  }, [parentTransactionId, isWaitingOnFunding, currency]);

  const railName = useMemo(() => {
    if (fundingParentTransaction?.TransactionType === TransactionType.EFT_FUNDING) {
      return t('FundingStatusBanner.Rail.eft', { defaultValue: 'EFT' });
    }
    if (fundingParentTransaction?.TransactionType === TransactionType.ACH_FUNDING) {
      return t('FundingStatusBanner.Rail.ach', { defaultValue: 'ACH' });
    }
    if (fundingParentTransaction?.TransactionType === TransactionType.CREDIT_CARD) {
      return t('FundingStatusBanner.Rail.creditCard', { defaultValue: 'Credit Card' });
    }
    return '';
  }, [fundingParentTransaction, t]);

  // Which funding option describes this parent's settlement time — 'bank' for EFT/ACH Funding,
  // 'credit' for Credit Card. Looking up 'bank' unconditionally would show the wrong settlement
  // time (or none) for a Credit Card parent.
  const fundingSource = fundingParentTransaction?.TransactionType === TransactionType.CREDIT_CARD ? 'credit' : 'bank';
  const matchingFundingOption = useMemo(
    () => fundingOptions.find((option) => option.Source === fundingSource),
    [fundingOptions, fundingSource],
  );
  const isInstantSettlement = matchingFundingOption?.SettlementTime === 'immediately';
  const settlementTime = useMemo(() => {
    if (!matchingFundingOption) return '';
    return getFundingSettlementTimeLabel(t, matchingFundingOption.SettlementTime);
  }, [matchingFundingOption, t]);

  // Payment's own pending-approval banner — unchanged from today.
  if (transactionStatus === TransactionStatus.PENDING_APPROVAL) {
    return (
      <ApprovalPanel
        className={className}
        title={t('Approval.statusPending', { defaultValue: 'Pending Approval' })}
        description={t('Approval.description', {
          defaultValue: 'Approvers can find these transaction(s) in their Approval Inbox',
        })}
      />
    );
  }

  if (!isWaitingOnFunding && !isPaymentFailed) return null;

  // ParentTransactionID isn't always a funding transaction (e.g. account transfers use it too) —
  // only treat it as one when its TransactionType is actually a funding type.
  const isFundingParent =
    fundingParentTransaction?.TransactionType === TransactionType.EFT_FUNDING ||
    fundingParentTransaction?.TransactionType === TransactionType.ACH_FUNDING ||
    fundingParentTransaction?.TransactionType === TransactionType.CREDIT_CARD;

  if (!fundingParentTransaction || !isFundingParent) return null;

  const fundingId = parentTransactionId;
  const boldComponent = { bold: <span style={{ fontWeight: 600 }} /> };

  const content = (() => {
    if (fundingParentTransaction.TransactionStatus === TransactionStatus.PENDING_APPROVAL) {
      return (
        <InfoBanner colorScheme="neutral">
          <Trans
            t={t}
            i18nKey="FundingStatusBanner.fundingPendingApproval"
            values={{ transactionId: fundingId }}
            defaults="This payment is waiting for funding transaction ID <bold>{{transactionId}}</bold> to be approved."
            components={boldComponent}
          />
        </InfoBanner>
      );
    }

    if (fundingParentTransaction.TransactionStatus === TransactionStatus.FAILED) {
      return (
        <InfoBanner colorScheme="error">
          <Trans
            t={t}
            i18nKey="FundingStatusBanner.fundingFailed"
            values={{ transactionId: fundingId }}
            defaults="Funding transaction ID <bold>{{transactionId}}</bold> failed — this payment could not be processed."
            components={boldComponent}
          />
        </InfoBanner>
      );
    }

    const isSettled =
      fundingParentTransaction.TransactionStatus === TransactionStatus.SETTLED ||
      fundingParentTransaction.TransactionStatus === TransactionStatus.SUCCESSFUL ||
      fundingParentTransaction.TransactionStatus === TransactionStatus.COMPLETE;

    if (isSettled && isPaymentFailed) {
      return (
        <InfoBanner colorScheme="error">
          <Trans
            t={t}
            i18nKey="FundingStatusBanner.fundingSettledPaymentFailed"
            values={{ transactionId: fundingId }}
            defaults="Funding transaction ID <bold>{{transactionId}}</bold> settled, but this payment failed for a separate reason."
            components={boldComponent}
          />
        </InfoBanner>
      );
    }

    if (isSettled) {
      return (
        <InfoBanner colorScheme="neutral">
          <Trans
            t={t}
            i18nKey="FundingStatusBanner.fundingSettled"
            values={{ transactionId: fundingId }}
            defaults="Funding transaction ID <bold>{{transactionId}}</bold> settled — this payment is being processed."
            components={boldComponent}
          />
        </InfoBanner>
      );
    }

    // Payment already failed but funding is still pending — an inconsistent state, but "will be
    // processed" (the fallback below) would be actively wrong, so use the failed copy instead.
    if (isPaymentFailed) {
      return (
        <InfoBanner colorScheme="error">
          <Trans
            t={t}
            i18nKey="FundingStatusBanner.fundingSettledPaymentFailed"
            values={{ transactionId: fundingId }}
            defaults="Funding transaction ID <bold>{{transactionId}}</bold> settled, but this payment failed for a separate reason."
            components={boldComponent}
          />
        </InfoBanner>
      );
    }

    return (
      <InfoBanner colorScheme="neutral">
        {eLinxRequestId ? (
          <Trans
            t={t}
            i18nKey="FundingStatusBanner.fundingPendingElinx"
            values={{ transactionId: fundingId, elinxId: eLinxRequestId }}
            defaults="This payment will be processed once funding transaction <bold>{{transactionId}}</bold> settles and eLinx request <bold>{{elinxId}}</bold> is completed."
            components={boldComponent}
          />
        ) : isInstantSettlement ? (
          <Trans
            t={t}
            i18nKey="FundingStatusBanner.fundingPendingInstant"
            values={{ transactionId: fundingId, railName }}
            defaults="This payment will be processed once funding transaction ID <bold>{{transactionId}}</bold> settles instantly via {{railName}}."
            components={boldComponent}
          />
        ) : (
          <Trans
            t={t}
            i18nKey="FundingStatusBanner.fundingPending"
            values={{ transactionId: fundingId, settlementTime, railName }}
            defaults="This payment will be processed once funding transaction ID <bold>{{transactionId}}</bold> settles — typically {{settlementTime}} via {{railName}}."
            components={boldComponent}
          />
        )}
      </InfoBanner>
    );
  })();

  return <div className={className}>{content}</div>;
}
